/**
 * CockpitCharts — Three Chart.js visualizations for the Chutt... agency cockpit
 * Uses ResizeObserver for reliable responsive behavior on window/container resize.
 */
import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import GlassCard from '../shared/GlassCard';
import { colors, platformColors } from '../../styles/colors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const FONT_FAMILY = '"DM Sans", sans-serif';

const commonTooltip = {
  backgroundColor: colors.charcoal,
  cornerRadius: 8,
  titleFont: { family: FONT_FAMILY, size: 12 },
  bodyFont: { family: FONT_FAMILY, size: 11 },
  padding: 10,
};

const gridColor = colors.borderLight;

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function SectionTitle({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Playfair Display", Georgia, serif',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: colors.charcoal,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Wrapper that uses ResizeObserver to force Chart.js resize on container change.
 * This fixes the "frozen chart" bug when toggling fullscreen/windowed.
 */
function ChartContainer({ children, height = 200 }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      // Find the canvas inside and trigger Chart.js resize
      const canvas = el.querySelector('canvas');
      if (canvas) {
        const chartInstance = ChartJS.getChart(canvas);
        if (chartInstance) {
          chartInstance.resize();
        }
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
}

// --- Bar: Scores par marque ---
function ScoresBar({ data, accent }) {
  const barColor = accent || colors.primary;

  const chartData = {
    labels: data.map((d) => d.name),
    datasets: [
      {
        data: data.map((d) => d.score),
        backgroundColor: hexToRgba(barColor, 0.6),
        borderRadius: 6,
        maxBarThickness: 48,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    plugins: {
      legend: { display: false },
      tooltip: commonTooltip,
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textMed },
      },
      y: {
        max: 100,
        grid: { color: gridColor },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textLight },
      },
    },
  };

  return (
    <Box>
      <SectionTitle>Scores par marque</SectionTitle>
      <ChartContainer height={200}>
        <Bar data={chartData} options={options} />
      </ChartContainer>
    </Box>
  );
}

// --- Line: Evolution portefeuille ---
function EvolutionLine({ data, accent }) {
  const mainColor = accent || colors.primary;

  const datasets = [
    {
      label: 'Moyenne',
      data: data.avg,
      borderColor: mainColor,
      backgroundColor: hexToRgba(mainColor, 0.1),
      fill: true,
      tension: 0.4,
      pointRadius: 2,
      borderWidth: 2,
    },
  ];

  if (data.best?.values) {
    datasets.push({
      label: data.best.name || 'Meilleur',
      data: data.best.values,
      borderColor: hexToRgba(mainColor, 0.4),
      borderDash: [6, 4],
      fill: false,
      tension: 0.4,
      pointRadius: 0,
      borderWidth: 1.5,
    });
  }

  if (data.worst?.values) {
    datasets.push({
      label: data.worst.name || 'Plus faible',
      data: data.worst.values,
      borderColor: hexToRgba(colors.danger, 0.4),
      borderDash: [6, 4],
      fill: false,
      tension: 0.4,
      pointRadius: 0,
      borderWidth: 1.5,
    });
  }

  const chartData = { labels: data.labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    plugins: {
      legend: { display: false },
      tooltip: commonTooltip,
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textMed },
      },
      y: {
        grid: { color: gridColor },
        ticks: { font: { family: FONT_FAMILY, size: 11 }, color: colors.textLight },
      },
    },
  };

  return (
    <Box>
      <SectionTitle>Evolution portefeuille</SectionTitle>
      <ChartContainer height={200}>
        <Line data={chartData} options={options} />
      </ChartContainer>
    </Box>
  );
}

const PLATFORM_LABELS = {
  twitter: 'Twitter/X',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  news: 'Actualites',
  forum: 'Forums',
  web: 'Web',
  youtube: 'YouTube',
  google_reviews: 'Google',
};

// Chart.js plugin: center text inside doughnut
const centerTextPlugin = {
  id: 'centerText',
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chart.config.options.plugins.centerText?.display) return;
    const { text, subText } = chart.config.options.plugins.centerText;
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Main value
    ctx.font = `800 1.75rem "DM Sans", sans-serif`;
    ctx.fillStyle = colors.charcoal;
    ctx.fillText(text, centerX, centerY - 8);

    // Sub label
    ctx.font = `400 0.7rem "DM Sans", sans-serif`;
    ctx.fillStyle = colors.gray400;
    ctx.fillText(subText, centerX, centerY + 14);

    ctx.restore();
  },
};

// --- Doughnut: Mentions par plateforme ---
function MentionsDoughnut({ data }) {
  const entries = Object.entries(data)
    .map(([key, val]) => ({
      platform: key.toLowerCase(),
      label: PLATFORM_LABELS[key.toLowerCase()] || key,
      value: val,
      color: platformColors[key.toLowerCase()] || colors.gray400,
    }))
    .filter((e) => e.value > 0);

  const total = entries.reduce((sum, e) => sum + e.value, 0);

  const chartData = {
    labels: entries.map((e) => e.label),
    datasets: [
      {
        data: entries.map((e) => e.value),
        backgroundColor: entries.map((e) => e.color),
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    cutout: '62%',
    animation: {
      animateRotate: true,
      duration: 800,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: { display: false },
      tooltip: commonTooltip,
      centerText: {
        display: true,
        text: total.toLocaleString('fr-FR'),
        subText: 'mentions',
      },
    },
  };

  return (
    <Box>
      <SectionTitle>Mentions par plateforme</SectionTitle>
      <ChartContainer height={220}>
        <Doughnut data={chartData} options={options} plugins={[centerTextPlugin]} />
      </ChartContainer>
      {/* Bottom legend matching Radar SegmentWheel */}
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
        {entries.slice(0, 6).map((e) => (
          <Box key={e.platform} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: e.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: colors.gray500, fontFamily: FONT_FAMILY }}>
              {e.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// --- Main component ---
function CockpitCharts({ charts, accentColor }) {
  const accent = accentColor || colors.primary;

  if (!charts) return null;

  const hasBoth = charts.scores_by_brand && charts.mentions_by_platform && Object.keys(charts.mentions_by_platform).length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Row 1: Bar + Doughnut side by side */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: hasBoth ? '1fr 1fr' : '1fr' },
          gap: 3,
        }}
      >
        {charts.scores_by_brand && (
          <GlassCard sx={{ p: 4, minWidth: 0, overflow: 'hidden' }}>
            <ScoresBar data={charts.scores_by_brand} accent={accent} />
          </GlassCard>
        )}
        {charts.mentions_by_platform && Object.keys(charts.mentions_by_platform).length > 0 && (
          <GlassCard sx={{ p: 4, minWidth: 0, overflow: 'hidden' }}>
            <MentionsDoughnut data={charts.mentions_by_platform} />
          </GlassCard>
        )}
      </Box>
      {/* Row 2: Evolution full width (dates need horizontal space) */}
      {charts.evolution && (
        <GlassCard sx={{ p: 4, minWidth: 0, overflow: 'hidden' }}>
          <EvolutionLine data={charts.evolution} accent={accent} />
        </GlassCard>
      )}
    </Box>
  );
}

export default CockpitCharts;
